// src/maps/DivesiteMap.js
import React, { useState, useEffect, useRef } from "react";
import {
  Map,
  AdvancedMarker,
  Pin,
  InfoWindow,
  useMap,
  useMapsLibrary,
} from "@vis.gl/react-google-maps";
import { MarkerClusterer } from "@googlemaps/markerclusterer";

import { Box, Typography, CircularProgress, Fab } from "@mui/material";
import MyLocationIcon from "@mui/icons-material/MyLocation";

// Import the glassmorphic components
import {
  GlassMapContainer,
  GlassRecenterButton,
  GlassInfoWindow,
  GlassSiteNameButton,
  GlassLoadingOverlay,
  GlassLocationStatus,
  GlassMapControls,
  GlassMapLegend,
  GlassZoomControls,
} from "../components/layouts/GlassMapLayout"; // Adjust import path as needed


// Ensure you have this new stub flag icon in your public folder
const STUB_ICON_URL = '/diver-down-flag-stub.png'; // For individual stubs AND stub clusters
const SITE_ICON_URL = '/diver-down-flag.png';   // For individual sites AND site clusters

const DivesiteMap = ({ sites = [], stubs = [], onMarkerClick, onNameClick }) => {
  const [openInfoWindow, setOpenInfoWindow] = useState(false);
  const [currentMapCenter, setCurrentMapCenter] = useState({
    lat: 47.6453539,
    lng: -122.5316133,
  });
  const [currentMapZoom, setCurrentMapZoom] = useState(10);
  const [selectedMarkerData, setSelectedMarkerData] = useState(null);
  const [userPinLocation, setUserPinLocation] = useState(null);
  const [isMyLocationInfoOpen, setIsMyLocationInfoOpen] = useState(false);
  const [initialLoad, setInitialLoad] = useState(true);
  const [locationStatus, setLocationStatus] = useState("loading"); // 'loading', 'success', 'error'
  const [isMapLoaded, setIsMapLoaded] = useState(false);
  const mapInstanceRef = useRef(null);

  // Refs for site markers and clusterer
  const siteClustererRef = useRef(null);
  const nativeSiteMarkersRef = useRef([]);

  // Refs for stub markers and clusterer
  const stubClustererRef = useRef(null);
  const nativeStubMarkersRef = useRef([]);

  const sitePoiList = (sites || [])
    .map((item) => ({
      key: `site-${item._id}`,
      title: item.uddf?.divesite?.site?.name || "Unnamed Site",
      location: {
        // UPDATED: Assuming sites use the same structure as stubs
        lat: parseFloat(item.uddf?.divesite?.site?.geography?.latitude),
        lng: parseFloat(item.uddf?.divesite?.site?.geography?.longitude),
      },
      details: item,
      isStub: false,
    }))
    .filter(poi =>
      poi.location &&
      typeof poi.location.lat === 'number' && !isNaN(poi.location.lat) &&
      typeof poi.location.lng === 'number' && !isNaN(poi.location.lng)
    );

    console.log(stubs)
  
    const stubPoiList = (stubs || [])
    .filter(item => item.stubInfo.status === 'new')
    .map((item) => ({
      key: `stub-${item._id}`,
      title: item.uddf?.divesite?.site?.name || "Unnamed divesite Bounty",
      location: {
        lat: parseFloat(item.uddf?.divesite?.site?.geography?.latitude),
        lng: parseFloat(item.uddf?.divesite?.site?.geography?.longitude),
      },
      details: item,
      isStub: true,
      

    }))
    .filter(poi => // This filter remains to ensure valid location data
      poi.location &&
      typeof poi.location.lat === 'number' && !isNaN(poi.location.lat) &&
      typeof poi.location.lng === 'number' && !isNaN(poi.location.lng)
    );

  const centerOnUserLocation = () => {
    setLocationStatus("loading");
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const newLocation = {
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          };
          const newZoom = 12;
          setCurrentMapCenter(newLocation);
          setCurrentMapZoom(newZoom);
          setUserPinLocation(newLocation);
          if (mapInstanceRef.current) {
            mapInstanceRef.current.panTo(newLocation);
          }
          setLocationStatus("success");
          setInitialLoad(false);
        },
        (error) => {
          console.error("Error getting current location:", error);

          setUserPinLocation(null);

          setInitialLoad(false);
        }
      );
    } else {

      console.warn("Geolocation is not supported by this browser.");
      setUserPinLocation(null);
      setInitialLoad(false);
    }
  };

  useEffect(() => {
    centerOnUserLocation();
  }, []);

  const handleCameraChanged = (ev) => {
    setCurrentMapZoom(ev.detail.zoom);
    setCurrentMapCenter(ev.detail.center);
  };

  const RecenterButton = () => (
    <Fab
      color="primary"
      size="medium"
      aria-label="recenter map"
      onClick={centerOnUserLocation}
      sx={{
        position: 'absolute',
        top: 'calc(50% + 40px)',
        right: 16,
        transform: 'translateY(-50%)',
        zIndex: 1000,
      }}
    >
      <MyLocationIcon />
    </Fab>
  );

  const MapInteractions = () => {
    const map = useMap();
    const markerLibrary = useMapsLibrary('marker');
    const coreLibrary = useMapsLibrary('core');

    useEffect(() => {
      if (map) {
        mapInstanceRef.current = map;
      }
    }, [map]);

    // Effect to set isMapLoaded when the map instance is ready and tiles have loaded
    useEffect(() => {
      if (map) {
        // mapInstanceRef.current is already being set in the effect above, which is fine.
        // Listen for the 'tilesloaded' event to consider the map truly "loaded" visually.
        const tilesLoadedListener = map.addListener('tilesloaded', () => {
          setIsMapLoaded(true);
        });

        // Cleanup the listener when the component unmounts or the map instance changes.
        return () => {
          if (tilesLoadedListener) {
            tilesLoadedListener.remove();
          }
        };
      }
    // setIsMapLoaded is stable from useState. Adding it for exhaustive-deps, though not strictly necessary
    // as MapInteractions is a closure over DivesiteMap's scope.
    }, [map, setIsMapLoaded]);

    useEffect(() => {

      if (
        !map ||
        !markerLibrary || typeof markerLibrary.Marker !== 'function' ||
        !coreLibrary || typeof coreLibrary.Size !== 'function' || typeof coreLibrary.Point !== 'function'
      ) {
        return;
      }

      const { Marker } = markerLibrary;
      const { Size, Point } = coreLibrary;

      const createIndividualMarkers = (poiArray, isStubType) => {
        return poiArray.map((poi) => {
          const iconUrl = isStubType ? STUB_ICON_URL : SITE_ICON_URL;
          const iconScaledSize = isStubType ? new Size(40, 26) : new Size(40, 26);
          const iconAnchor = isStubType ? new Point(15, 30) : new Point(16, 20);

          const nativeMarker = new Marker({
            position: poi.location,
            title: poi.title,
            icon: { url: iconUrl, scaledSize: iconScaledSize, anchor: iconAnchor }
          });

          nativeMarker.addListener('click', () => {
            setSelectedMarkerData(poi);
            setOpenInfoWindow(true);
            if (typeof onMarkerClick === 'function') {
              onMarkerClick(poi.details);
            }
          });
          return nativeMarker;
        });
      };

      const setupClusterer = (clustererInstanceRef, nativeMarkersArray, clusterIconUrl) => {
  if (nativeMarkersArray.length > 0) {
    clustererInstanceRef.current = new MarkerClusterer({
      markers: nativeMarkersArray,
      map: map,
      renderer: {
        render: ({ count, position }) => {
          const flagIconSize = new Size(40, 26);
          const flagIconAnchor = new Point(flagIconSize.width / 2, flagIconSize.height / 2);
          const maxZIndex = typeof Marker.MAX_ZINDEX === 'number' ? Marker.MAX_ZINDEX : 999999;
          
          // Determine font size based on count (optional)
          let fontSize = '16px'; // Default larger size
          if (count > 99) fontSize = '14px'; // Slightly smaller for 3-digit numbers
          if (count > 999) fontSize = '12px'; // Even smaller for 4+ digit numbers
          
          return new Marker({
            position,
            icon: {
              url: clusterIconUrl,
              scaledSize: flagIconSize,
              anchor: flagIconAnchor,
              labelOrigin: new Point(flagIconSize.width / 2, flagIconSize.height / 2 + 4),
            },
            label: { 
              text: String(count), 
              color: 'black', 
              fontSize: fontSize, // Changed from '12px' to variable
              fontWeight: 'bold' 
            },
            zIndex: maxZIndex + count,
          });
        }
      }
    });
  }
};


      if (siteClustererRef.current) siteClustererRef.current.clearMarkers();
      nativeSiteMarkersRef.current.forEach(marker => marker.setMap(null));
      nativeSiteMarkersRef.current = [];

      if (stubClustererRef.current) stubClustererRef.current.clearMarkers();
      nativeStubMarkersRef.current.forEach(marker => marker.setMap(null));
      nativeStubMarkersRef.current = [];

      if (sitePoiList.length > 0) {
        const newNativeSiteMarkers = createIndividualMarkers(sitePoiList, false);
        nativeSiteMarkersRef.current = newNativeSiteMarkers;
        setupClusterer(siteClustererRef, newNativeSiteMarkers, SITE_ICON_URL);
      }

      if (stubPoiList.length > 0) {
        const newNativeStubMarkers = createIndividualMarkers(stubPoiList, true);
        nativeStubMarkersRef.current = newNativeStubMarkers;
        setupClusterer(stubClustererRef, newNativeStubMarkers, STUB_ICON_URL);
      }

      return () => {
        if (siteClustererRef.current) siteClustererRef.current.clearMarkers();
        nativeSiteMarkersRef.current.forEach(marker => marker.setMap(null));

        if (stubClustererRef.current) stubClustererRef.current.clearMarkers();
        nativeStubMarkersRef.current.forEach(marker => marker.setMap(null));
      };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        map, markerLibrary, coreLibrary,
        JSON.stringify(sitePoiList),
        JSON.stringify(stubPoiList)
    ]);

    return (
      <>
        {/* Glassmorphic Location Status - positioned to avoid Google controls */}
        <Box
          sx={{
            position: "absolute",
            top: 80, // Move below Google's controls
            left: 20,
            zIndex: 1000,
          }}
        >
          <GlassLocationStatus status={locationStatus}>
          </GlassLocationStatus>
        </Box>

        {/* Glassmorphic Recenter Button - positioned to avoid Google controls */}
        <Box
          sx={{
            position: "absolute",
            bottom: 120, // Move above legend and Google controls
            right: 20,
            zIndex: 1000,
          }}
        >
          <GlassRecenterButton
            onClick={centerOnUserLocation}
            aria-label="recenter map"
            title="Center on my location"
          >
            <MyLocationIcon />
          </GlassRecenterButton>
        </Box>


        {userPinLocation && (

          <AdvancedMarker
            position={userPinLocation}
            onClick={() => setIsMyLocationInfoOpen(true)}
            title={initialLoad ? "Loading location..." : "You are Here"}
            zIndex={1001}
          >
            <Pin background={"#00C853"} glyphColor={"#FFFFFF"} borderColor={"#007E33"} />
          </AdvancedMarker>
        )}

        {/* Glassmorphic Info Window for Selected Dive Site */}
        {openInfoWindow && selectedMarkerData && (
          <InfoWindow
            position={selectedMarkerData.location}
            onCloseClick={() => {
              setOpenInfoWindow(false);
              setSelectedMarkerData(null);
            }}
          >
            <GlassInfoWindow>
              <Typography
                variant="body2"
                sx={{ color: "text.secondary", mb: 1 }}
              >
                Dive Site
              </Typography>
              <GlassSiteNameButton
                onClick={() => {
                    if (typeof onNameClick === 'function') {
                        onNameClick(selectedMarkerData.details);
                    }
                }}
                sx={{
                  border: 'none', background: 'none', cursor: 'pointer', padding: 0,
                  fontWeight: 'bold', fontSize: '1em', color: 'primary.main',
                  '&:hover': { textDecoration: 'underline' }
                }}
              >
                {selectedMarkerData.title} {selectedMarkerData.isStub ? "(Stub)" : ""}
              </GlassSiteNameButton>
            </GlassInfoWindow>
          </InfoWindow>
        )}

        {isMyLocationInfoOpen && userPinLocation && (
          <InfoWindow
            position={userPinLocation}
            onCloseClick={() => setIsMyLocationInfoOpen(false)}
          >

            <GlassInfoWindow>
              <Typography
                variant="body2"
                sx={{ color: "text.secondary", textAlign: "center" }}
              >
                {initialLoad ? "Initial Location" : "You are here!"}
              </Typography>
            </GlassInfoWindow>
          </InfoWindow>
        )}

        {/* Glassmorphic Map Legend - positioned to avoid Google controls */}
        {/*
        <Box
          sx={{
            position: "absolute",
            bottom: 60, // Move above Google's controls
            left: 20,
            zIndex: 1000,
          }}
        >
          <GlassMapLegend>
            <div className="legend-title">Map Legend</div>
            <div className="legend-item">
              <div
                className="legend-icon"
                style={{
                  background: "linear-gradient(45deg, #ff0000, #ffffff)",
                  border: "1px solid #000",
                }}
              />
              <span>Dive Sites</span>
            </div>
            <div className="legend-item">
              <div
                className="legend-icon"
                style={{
                  background: "#008000",
                  borderRadius: "50%",
                }}
              />
              <span>Your Location</span>
            </div>
          </GlassMapLegend>
        </Box>
        */}
      </>
    );
  };

  return (
    <GlassMapContainer>
      {/* Glassmorphic Loading Overlay */}
      {!isMapLoaded && (
        <GlassLoadingOverlay>
          <CircularProgress size={40} />
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            Loading dive sites...
          </Typography>
        </GlassLoadingOverlay>
      )}

      <Map
        zoom={currentMapZoom}
        center={currentMapCenter}
        gestureHandling="greedy"
        mapId="ea87b08ae3ef203"
        onCameraChanged={handleCameraChanged}
        style={{ width: "100%", height: "100%", borderRadius: "inherit" }}
      >
        <MapInteractions />
      </Map>
    </GlassMapContainer>
  );
};

export default DivesiteMap;
